import {
	AfterViewChecked,
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnInit,
	Output,
	ViewEncapsulation,
	inject,
	signal
} from '@angular/core';
import { filter, takeUntil } from 'rxjs/operators';
import { IPageInfo, VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { IGridCell, IGridColumn, IGridDataConfig, IGridHeaderConfig, IGridRow } from './interfaces';
import { gridActionclearActiveCells$ } from './actions';
import { GridService } from './grid.service';
import { BaseComponent } from '../base.component';
import { NHAGridShowGroupRowPipe } from './pipes/show-group-row.pipe';
import { NHAGridHtmlIsFuncPipe } from './pipes/html-is-func.pipe';
import { NHAGridEvalRowsPipe } from './pipes/eval-rows.pipe';
import { NHAGridEvalSizePipe } from './pipes/eval-size.pipe';
import { TemplateFunctionPipe } from '../../pipes/template-function.pipe';
import { CommonModule } from '@angular/common';
import { NHATestDirective } from '../../directives/test/test.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { cellTracker, columnTracker, rowTracker } from './functions/track-by.function';

@Component({
	selector: 'nha-grid-v3',
	templateUrl: './grid.component.html',
	styleUrls: ['./grid.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [GridService],
	exportAs: 'grid',
	imports: [
		NHATestDirective,
		CommonModule,
		VirtualScrollerModule,
		TemplateFunctionPipe,
		NHAGridEvalSizePipe,
		NHAGridEvalRowsPipe,
		NHAGridHtmlIsFuncPipe,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		NHAGridShowGroupRowPipe
	]
})
export class NHAGridComponent extends BaseComponent implements OnInit, AfterViewChecked, AfterViewInit {
	private _gridRef = inject(ElementRef);
	private _gridService = inject(GridService);

	private _grid: HTMLElement = null;
	private _headerConfig: IGridHeaderConfig;
	private _dataConfig: IGridDataConfig;
	private _dataItems: unknown[];
	private _dataUpdated: boolean = false;
	private _configUpdated: boolean = false;

	@Input() baseCellWidth: string = '100px';
	@Input() baseCellHeight: string = '40px';
	@Input() widthMode: 'fixed' | 'fill' = 'fixed';
	@Input() enableKeyboardNavigation: boolean = false;
	@Input() enableVirtualScroll: boolean = false;
	@Input() enablePinnedShadow: boolean = true;
	@Input() enableCollapsibleColumns: boolean = false;

	private _resizeObserver: ResizeObserver | null = null;
	private _dataResizeObserver: ResizeObserver | null = null;
	private _appliedWidthOverrides: number[] = [];
	private _rafId: number = 0;
	private _lastWidth: number = -1;
	private _cachedCells: { units: number; minPx: number }[] | null = null;

	@Input()
	set enableHover(hover: boolean) {
		this._hover = hover;
	}
	get enableHover(): boolean {
		return this._hover;
	}

	@Input()
	set hasError(error: boolean) {
		this._hasError = error;
		this._gridService.setHasError(error);
	}
	get hasError(): boolean {
		return this._hasError;
	}

	@Input()
	set headerConfig(headerConfig: IGridHeaderConfig) {
		this._headerConfig = headerConfig;
		this._configUpdated = true;
		this._cachedCells = null;
		this.applyCellWidthBase();
	}
	get headerConfig(): IGridHeaderConfig {
		if (!this._headerConfig) {
			throw new Error('No Header Configuration Supplied');
		}
		return this._headerConfig;
	}

	@Input()
	get dataConfig(): IGridDataConfig {
		if (!this._dataConfig) {
			throw new Error('No Data Configuration Supplied');
		}

		if (!this._dataConfig.dataRowHeight) {
			this._dataConfig.dataRowHeight = 1;
		}

		return this._dataConfig;
	}
	set dataConfig(dataConfig: IGridDataConfig) {
		this._dataConfig = dataConfig;
		this._configUpdated = true;

		if (dataConfig.group) {
			this.enablePinnedShadow = false;
		}
	}

	@Input()
	set dataItems(items: unknown[]) {
		this._dataItems = items;
		this._dataUpdated = true;
		NHAGridShowGroupRowPipe.clearGroups();
	}
	get dataItems(): unknown[] {
		return this._dataItems;
	}

	@Output() cellActivated: EventEmitter<HTMLElement> = this._gridService.getCellActivated();
	@Output() virtualScrollUpdate: EventEmitter<unknown> = new EventEmitter<unknown>();
	@Output() virtualScrollChange: EventEmitter<IPageInfo> = new EventEmitter<IPageInfo>();
	@Output() virtualScrollEnd: EventEmitter<IPageInfo> = new EventEmitter<IPageInfo>();

	@HostBinding('class.hover') _hover: boolean = false;
	@HostBinding('class.has-error') _hasError: boolean = false;
	@HostBinding('class.width-mode-fill') get _isFillMode(): boolean {
		return this.widthMode === 'fill';
	}

	ngOnInit(): void {
		this._grid = this._gridRef.nativeElement;
		this.applyCellWidthBase();
		this._grid.style.setProperty('--cell-height1', this.baseCellHeight);

		this._gridService.initialize(this._gridRef);

		if (this.widthMode === 'fill' && typeof ResizeObserver !== 'undefined') {
			this._resizeObserver = new ResizeObserver(() => this.scheduleResizeRecompute());
			this._resizeObserver.observe(this._grid);
		}
	}

	override ngOnDestroy(): void {
		this._resizeObserver?.disconnect();
		this._dataResizeObserver?.disconnect();
		this._resizeObserver = null;
		this._dataResizeObserver = null;
		if (this._rafId) {
			cancelAnimationFrame(this._rafId);
			this._rafId = 0;
		}
		super.ngOnDestroy();
	}

	private scheduleResizeRecompute(): void {
		if (this._rafId) {
			return;
		}
		this._rafId = requestAnimationFrame(() => {
			this._rafId = 0;
			if (!this._grid) {
				return;
			}
			const w = this.computeAvailableWidth();
			if (w === this._lastWidth) {
				return;
			}
			this.applyCellWidthBase();
		});
	}

	private applyCellWidthBase(): void {
		if (!this._grid) {
			return;
		}

		this.clearWidthOverrides();
		this._lastWidth = this.computeAvailableWidth();

		if (this.widthMode === 'fill' && this._headerConfig?.columns?.length) {
			const cells = this._cachedCells ?? (this._cachedCells = this.buildCells());
			if (cells.length > 0 && this._lastWidth > 0) {
				this.applyFillWidths(cells, this._lastWidth);
				return;
			}
		}

		this._grid.style.setProperty('--cell-width1', this.baseCellWidth);
	}

	/**
	 * Available width for column distribution: host clientWidth minus the data
	 * area's vertical scrollbar. Without subtracting the scrollbar, virtual-scroller's
	 * y-bar pushes the rows past the visible area and a horizontal scrollbar appears.
	 */
	private computeAvailableWidth(): number {
		return Math.max(this._grid.clientWidth - this.measureDataScrollbarWidth(), 0);
	}

	private measureDataScrollbarWidth(): number {
		const el = this.getDataScrollElement();
		if (!el) {
			return 0;
		}
		return Math.max(el.offsetWidth - el.clientWidth, 0);
	}

	private getDataScrollElement(): HTMLElement | null {
		if (!this._grid) {
			return null;
		}
		return this.enableVirtualScroll
			? (this._grid.querySelector('virtual-scroller') as HTMLElement | null)
			: (this._grid.querySelector('.grid-data') as HTMLElement | null);
	}

	private buildCells(): { units: number; minPx: number }[] {
		const fallbackPerUnitMin = this.parsePxLength(this.baseCellWidth);
		const cells: { units: number; minPx: number }[] = [];

		for (const col of this._headerConfig.columns) {
			const cell = col.rows?.[0]?.cells?.[0];
			const units = typeof cell?.width === 'number' ? cell.width : 0;
			if (units <= 0) {
				continue;
			}
			const explicitMin = this.resolveMinPx(cell?.minWidth);
			const minPx = explicitMin > 0 ? explicitMin : units * fallbackPerUnitMin;
			cells.push({ units, minPx });
		}
		return cells;
	}

	private applyFillWidths(cells: { units: number; minPx: number }[], containerWidth: number): void {
		const widths = this.distributeFlex(cells, containerWidth);

		// If multiple columns share the same units value, they share --cell-widthN;
		// pick the larger so neither column underflows.
		const widthByUnits = new Map<number, number>();
		for (let i = 0; i < cells.length; i++) {
			const u = cells[i].units;
			widthByUnits.set(u, Math.max(widthByUnits.get(u) ?? 0, widths[i]));
		}

		// baseCellWidth still acts as the fallback for any cells whose unit value
		// isn't in the override map (shouldn't happen in practice).
		this._grid.style.setProperty('--cell-width1', this.baseCellWidth);
		widthByUnits.forEach((px, units) => {
			this._grid.style.setProperty(`--cell-width${units}`, `${px}px`);
			this._appliedWidthOverrides.push(units);
		});
	}

	/**
	 * Flex-style distribution: each column starts as proportional to its `units`. If
	 * that would drop a column below its `minPx`, pin it at `minPx` and redistribute
	 * the remaining width to the remaining columns. Repeat until stable. When all
	 * columns are pinned at their mins and the total exceeds the container, the
	 * grid's `overflow-x: auto` takes over and shows a horizontal scrollbar.
	 */
	private distributeFlex(cells: { units: number; minPx: number }[], containerWidth: number): number[] {
		const widths = new Array<number>(cells.length).fill(0);
		const pinned = new Array<boolean>(cells.length).fill(false);
		let remainingWidth = containerWidth;

		// Iteratively pin columns that would underflow. Bounded by cells.length iterations.
		for (let iter = 0; iter < cells.length; iter++) {
			const flexIndices = cells.map((_, i) => i).filter((i) => !pinned[i]);
			if (flexIndices.length === 0) {
				break;
			}
			const remainingUnits = flexIndices.reduce((s, i) => s + cells[i].units, 0);
			const perUnit = remainingUnits > 0 ? Math.max(remainingWidth, 0) / remainingUnits : 0;
			let pinnedAny = false;
			for (const i of flexIndices) {
				if (cells[i].units * perUnit < cells[i].minPx) {
					widths[i] = cells[i].minPx;
					pinned[i] = true;
					remainingWidth -= cells[i].minPx;
					pinnedAny = true;
				}
			}
			if (!pinnedAny) {
				for (const i of flexIndices) {
					widths[i] = cells[i].units * perUnit;
				}
				break;
			}
		}

		// Any cell still flagged flex-but-unset (defensive) gets its min.
		for (let i = 0; i < cells.length; i++) {
			if (widths[i] <= 0) {
				widths[i] = cells[i].minPx;
			}
		}
		return widths;
	}

	private clearWidthOverrides(): void {
		if (!this._grid || this._appliedWidthOverrides.length === 0) {
			return;
		}
		for (const units of this._appliedWidthOverrides) {
			this._grid.style.removeProperty(`--cell-width${units}`);
		}
		this._appliedWidthOverrides = [];
	}

	private resolveMinPx(value: number | string | undefined): number {
		if (typeof value === 'number') {
			return value > 0 ? value : 0;
		}
		if (typeof value === 'string') {
			return this.parsePxLength(value);
		}
		return 0;
	}

	private parsePxLength(value: string): number {
		const match = /^\s*([\d.]+)px\s*$/.exec(value);
		return match ? parseFloat(match[1]) : 0;
	}

	ngAfterViewInit(): void {
		this._gridService.configureScrolling();

		if (this.widthMode === 'fill' && typeof ResizeObserver !== 'undefined') {
			const dataScrollEl = this.getDataScrollElement();
			if (dataScrollEl) {
				this._dataResizeObserver = new ResizeObserver(() => this.scheduleResizeRecompute());
				this._dataResizeObserver.observe(dataScrollEl);
			}
		}

		gridActionclearActiveCells$.pipe(filter(Boolean), takeUntil(this.componentDestroyed$)).subscribe(() => {
			this._gridService.removeActiveCellState();
		});

		super.ngAfterViewInit();
	}

	ngAfterViewChecked(): void {
		if (this._dataUpdated && this.enableKeyboardNavigation && !this.enableVirtualScroll) {
			this._gridService.configureKeyboardNavigation();
		}
		if (this._configUpdated) {
			this._gridService.scrollIntoView();
			if (this.enableKeyboardNavigation && !this.enableVirtualScroll) {
				this._gridService.configureKeyboardNavigation();
			}
		}
		if (this._dataUpdated && this.enableHover) {
			this._hover = true;
		}
		this._configUpdated = false;
		this._dataUpdated = false;
	}

	collapsedGroups = signal<Set<string>>(new Set());

	toggleGroupCollapse(groupKey: string): void {
		this.collapsedGroups.update((current) => {
			const next = new Set(current);
			if (next.has(groupKey)) {
				next.delete(groupKey);
			} else {
				next.add(groupKey);
			}
			return next;
		});
	}

	getGroupKey(item: unknown): string {
		const key = this.dataConfig.group.key;
		return typeof key === 'function' ? key(item) : key;
	}

	expandCollapseColumn(headerColumn: IGridColumn, columnIndex: number): void {
		const column = this.dataConfig.columns[columnIndex];
		column.isCollapsed = !column.isCollapsed;
		headerColumn.isCollapsed = !headerColumn.isCollapsed;
	}

	trackColumns(item: unknown): (index: number, column: IGridColumn) => string {
		return columnTracker.bind(item);
	}

	trackRows(item: unknown): (index: number, row: IGridRow) => string {
		return rowTracker.bind(item);
	}

	trackCells(item: unknown): (index: number, cell: IGridCell) => string {
		return cellTracker.bind(item);
	}

	trackDataItems = (index: number, item: unknown): string => {
		if (this.dataConfig?.trackDataRowsBy) {
			return this.dataConfig.trackDataRowsBy(index, item);
		}

		return `item_${index}`;
	};

	trackHeaderColumns = (column: IGridColumn, index: number): string => {
		return columnTracker.call(null, index, column);
	};

	trackHeaderRows = (row: IGridRow, index: number): string => {
		return rowTracker.call(null, index, row);
	};

	trackHeaderCells = (cell: IGridCell, index: number): string => {
		return cellTracker.call(null, index, cell);
	};
}
